import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MapPin, Briefcase, Clock, ArrowRight, Search } from "lucide-react";
import { careersAPI } from "../../services/api";
import Seo from "../../components/Seo";

const LANG_LABELS = { fr: "Français", en: "English", ar: "العربية" };

export default function CareersJobList() {
  const { t } = useTranslation();
  const { companyName } = useOutletContext() || {};
  const [search, setSearch] = useState("");
  const [langFilter, setLangFilter] = useState("");

  const { data: postings = [], isLoading } = useQuery({
    queryKey: ["careers-postings", langFilter],
    queryFn: () => careersAPI.listPostings(langFilter ? { language: langFilter } : {}).then((r) => r.data),
  });

  const filtered = postings.filter((p) => {
    if (!search) return true;
    return (
      p.title.toLowerCase().includes(search.toLowerCase()) ||
      p.description.toLowerCase().includes(search.toLowerCase())
    );
  });

  return (
    <>
      <Seo
        title="Offres d'emploi — Mawered SIRH"
        description="Rejoignez l'équipe Mawered. Découvrez nos offres d'emploi en RH, ingénierie et commercial."
        path="/careers"
      />
    <div className="space-y-10">
      {/* Hero */}
      <div className="space-y-3">
        <p
          className="text-xs font-medium uppercase tracking-widest"
          style={{ color: "#5e6ad2", letterSpacing: "0.12em" }}
        >
          {t("careers.list.eyebrow")}
        </p>
        <h1
          className="text-4xl font-semibold"
          style={{ color: "var(--lm-ink)", letterSpacing: "-1px", lineHeight: 1.1 }}
        >
          {companyName ? t("careers.list.heroWithCompany", { company: companyName }) : t("careers.list.heroNoCompany")}
        </h1>
        <p className="text-base" style={{ color: "var(--lm-ink-2)", lineHeight: 1.6 }}>
          {t("careers.list.subtitle")}
        </p>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div
          className="flex items-center gap-2 flex-1 px-3 py-2 rounded-lg"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
        >
          <Search className="h-4 w-4 flex-shrink-0" style={{ color: "var(--lm-ink-3)" }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("careers.list.searchPlaceholder")}
            className="flex-1 bg-transparent text-sm outline-none"
            style={{ color: "var(--lm-ink)" }}
          />
        </div>
        <select
          value={langFilter}
          onChange={(e) => setLangFilter(e.target.value)}
          className="px-3 py-2 rounded-lg text-sm"
          style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)", color: "var(--lm-ink)" }}
        >
          <option value="">{t("careers.list.allLanguages")}</option>
          {["fr", "en", "ar"].map((l) => (
            <option key={l} value={l}>{LANG_LABELS[l]}</option>
          ))}
        </select>
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-24 rounded-xl animate-pulse"
              style={{ backgroundColor: "var(--lm-s1)" }}
            />
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <div className="py-20 text-center space-y-2">
          <Briefcase className="h-10 w-10 mx-auto" style={{ color: "var(--lm-ink-3)" }} />
          <p className="text-sm" style={{ color: "var(--lm-ink-3)" }}>
            {search ? t("careers.list.noResults") : t("careers.list.noPostings")}
          </p>
        </div>
      ) : (
        <div
          className="rounded-xl overflow-hidden"
          style={{ border: "1px solid var(--lm-border)" }}
        >
          {filtered.map((posting, idx) => (
            <Link
              key={posting.id}
              to={`/careers/${posting.id}`}
              className="flex items-center gap-4 px-6 py-5 no-underline transition-all group"
              style={{
                backgroundColor: "var(--lm-s1)",
                borderBottom: idx < filtered.length - 1 ? "1px solid var(--lm-border)" : "none",
                display: "flex",
              }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--lm-s2)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "var(--lm-s1)"; }}
            >
              <div className="flex-1 min-w-0 space-y-1">
                <h2 className="text-sm font-semibold" style={{ color: "var(--lm-ink)" }}>
                  {posting.title}
                </h2>
                <div className="flex items-center gap-4 flex-wrap">
                  {posting.contract_type && (
                    <MetaTag icon={Briefcase} label={posting.contract_type} />
                  )}
                  {posting.country_code && (
                    <MetaTag icon={MapPin} label={posting.country_code} />
                  )}
                  {posting.experience_required && (
                    <MetaTag icon={Clock} label={posting.experience_required} />
                  )}
                  {(posting.salary_min || posting.salary_max) && (
                    <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
                      {posting.salary_min && Number(posting.salary_min).toLocaleString()}
                      {posting.salary_max && ` — ${Number(posting.salary_max).toLocaleString()}`}
                      {" "}{posting.currency}
                    </span>
                  )}
                </div>
              </div>
              <ArrowRight
                className="h-4 w-4 flex-shrink-0 transition-transform group-hover:translate-x-1"
                style={{ color: "var(--lm-ink-3)" }}
              />
            </Link>
          ))}
        </div>
      )}

      <p className="text-xs text-center" style={{ color: "var(--lm-ink-3)" }}>
        {t("careers.list.countLabel", { count: filtered.length })}
      </p>
    </div>
    </>
  );
}

function MetaTag({ icon: Icon, label }) {
  return (
    <span className="flex items-center gap-1 text-xs" style={{ color: "var(--lm-ink-3)" }}>
      <Icon className="h-3 w-3" />
      {label}
    </span>
  );
}
